import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alert, Button, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import {
  CHAT_REVIEW_BATCH_SIZE,
  ChatAccountFilter,
  ChatReviewRow,
  useChatReviewList,
} from '~/hooks/apis/trip-planner/useChatReviewList';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { formatDateSlashesWithClockInSydney } from '~/services/TimeService';
import type { ChatOriginSurface } from '~/services/TripPlannerService';
import UsersService from '~/services/UsersService';

import TripPlannerChatSearchForm from './components/TripPlannerChatSearchForm';
import { TripPlannerDataGrid } from './components/TripPlannerDataGrid';
import TripSpoofDialog from './components/TripSpoofDialog';
import { chatSurfacePath } from './components/chatSurfaceLink';
import { TripPlannerChatSearchParams, useTripPlannerChatSearchParams } from './useTripPlannerChatSearchParams';

export default function TripPlannerChatConversationsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const gridApiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();
  const { tenant } = useCurrentTenant();
  const { searchParams } = useTripPlannerChatSearchParams();
  const params = new URLSearchParams(location.search);
  const account: ChatAccountFilter =
    params.get('account') === 'registered' ? 'registered' : params.get('account') === 'guest' ? 'guest' : 'all';
  const minMessages = Math.max(0, Math.floor(Number(params.get('minMessages')) || 0));
  const targetCount = Math.max(CHAT_REVIEW_BATCH_SIZE, Number(params.get('reviewLimit')) || CHAT_REVIEW_BATCH_SIZE);
  const origin = params.get('originSurface');
  const originSurface: ChatOriginSurface | undefined =
    origin === 'trip_planner' ||
    origin === 'accommodation_offer_page' ||
    origin === 'search_page' ||
    origin === 'unknown'
      ? origin
      : undefined;
  const query = useChatReviewList({ ...searchParams, account, minMessages, originSurface, brand: tenant.brand });
  const [paused, setPaused] = useState(false);
  const [spoof, setSpoof] = useState({ url: '', name: '', copied: false });
  const rows = useMemo(
    () =>
      Array.from(new Map((query.data?.pages.flatMap((page) => page.rows) ?? []).map((row) => [row.id, row])).values()),
    [query.data],
  );
  const scanned = query.data?.pages.reduce((count, page) => count + page.scanned, 0) ?? 0;
  const searching = !paused && !query.isError && (query.isFetching || (rows.length < targetCount && query.hasNextPage));
  const { fetchNextPage, hasNextPage, isFetching, isError } = query;

  useEffect(() => {
    if (!paused && !isFetching && !isError && hasNextPage && rows.length < targetCount) {
      void fetchNextPage();
    }
  }, [paused, isFetching, isError, hasNextPage, rows.length, targetCount, fetchNextPage]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(location.search);
    next.set(key, value);
    next.delete('page');
    next.delete('reviewLimit');
    setPaused(false);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };
  const handleSearch = (values: TripPlannerChatSearchParams) => {
    const next = new URLSearchParams(location.search);
    for (const key of ['chatId', 'userId'] as const) {
      if (values[key]) {
        next.set(key, values[key]);
      } else {
        next.delete(key);
      }
    }
    next.delete('reviewLimit');
    next.delete('page');
    setPaused(false);
    navigate({ pathname: location.pathname, search: next.toString() });
  };
  const loadMore = () => {
    const next = new URLSearchParams(location.search);
    next.set('reviewLimit', String(rows.length + CHAT_REVIEW_BATCH_SIZE));
    setPaused(false);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };
  const spoofChat = async (chat: ChatReviewRow) => {
    try {
      const url = await UsersService.spoofUserTrip(chat.createdBy, tenant, chatSurfacePath(chat.id, chat.origin));
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        enqueueSnackbar('Copy the spoof link from the dialog.', { variant: 'info' });
      }
      setSpoof({ url, name: chat.name, copied });
    } catch {
      enqueueSnackbar('Could not generate a spoof link. Please try again.', { variant: 'error' });
    }
  };
  const chatUrl = (id: number) => {
    const traceSearch = new URLSearchParams(location.search);
    traceSearch.delete('chatId');
    const search = traceSearch.toString();
    return `/trip-planner/${id}${search ? `?${search}` : ''}`;
  };
  const openChat = (row: ChatReviewRow) => {
    navigate(chatUrl(row.id), { state: { reviewIds: gridApiRef.current.getSortedRowIds().map(Number) } });
  };
  const chatCellLink = (row: ChatReviewRow, content: React.ReactNode) => (
    <Link
      href={chatUrl(row.id)}
      color="inherit"
      underline="none"
      sx={{ display: 'block', width: '100%' }}
      onClick={(event) => {
        event.stopPropagation();
        if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          openChat(row);
        }
      }}
    >
      {content}
    </Link>
  );
  const columns: Array<GridColDef<ChatReviewRow>> = [
    { field: 'id', headerName: 'Chat ID', width: 100, renderCell: ({ row }) => chatCellLink(row, row.id) },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200, renderCell: ({ row }) => chatCellLink(row, row.name) },
    ...(['firstUserMessage', 'secondUserMessage'] as const).map<GridColDef<ChatReviewRow>>((field) => ({
      field,
      headerName: field === 'firstUserMessage' ? 'First user message' : 'Second user message',
      flex: 1,
      minWidth: 250,
      display: 'flex',
      renderCell: ({ row }) =>
        chatCellLink(
          row,
          <Typography
            variant="body2"
            sx={{ py: 1, maxHeight: 250, overflowY: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {row[field] || '—'}
          </Typography>,
        ),
    })),
    ...(['createdAt', 'updatedAt'] as const).map<GridColDef<ChatReviewRow>>((field) => ({
      field,
      headerName: field === 'createdAt' ? 'Created (Sydney)' : 'Updated (Sydney)',
      width: 190,
      display: 'flex',
      valueFormatter: (value: string) => (value ? formatDateSlashesWithClockInSydney(value) : ''),
      renderCell: ({ row }) => chatCellLink(row, row[field] ? formatDateSlashesWithClockInSydney(row[field]) : ''),
    })),
    {
      field: 'origin',
      headerName: 'Origin',
      width: 150,
      valueGetter: (_value, row) =>
        row.origin?.surface === 'accommodation_offer_page'
          ? 'Offer page'
          : row.origin?.surface === 'trip_planner'
            ? 'Trip Planner'
            : row.origin?.surface === 'search_page'
              ? 'Search page'
              : '',
      renderCell: ({ row, value }) => chatCellLink(row, value),
    },
    {
      field: 'userMessages',
      headerName: 'User msgs',
      type: 'number',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => chatCellLink(row, row.userMessages),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      display: 'flex',
      renderCell: ({ row }) => {
        if (!row.registeredUser) {
          return (
            <Typography variant="body2" color="text.secondary">
              Guest
            </Typography>
          );
        }

        return (
          <Stack alignItems="flex-start" spacing={0}>
            <Link href={'/users/' + row.createdBy} onClick={(event) => event.stopPropagation()}>
              View user
            </Link>
            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
              <Link
                component="button"
                onClick={(event) => {
                  event.stopPropagation();
                  void spoofChat(row);
                }}
              >
                Spoof chat
              </Link>
            </PermissionedComponent>
          </Stack>
        );
      },
    },
  ];
  return (
    <>
      <TripPlannerChatSearchForm
        initialValues={searchParams}
        onSearch={handleSearch}
        originSurface={originSurface ?? 'all'}
        onOriginSurfaceChange={(value) => updateFilter('originSurface', value)}
        account={account}
        minMessages={minMessages}
        onAccountChange={(value) => updateFilter('account', value)}
        onMinMessagesChange={(value) => updateFilter('minMessages', value)}
      />
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} matches · {scanned.toLocaleString()} chats checked
          {query.data?.pages[0] ? ' of ' + query.data.pages[0].total.toLocaleString() : ''}
        </Typography>
        {searching && (
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setPaused(true)}>
            Pause search
          </Button>
        )}
      </Stack>
      {searching && <LinearProgress aria-label="Searching chats across pages" sx={{ mb: 1 }} />}
      {query.isError && (
        <Alert
          severity="error"
          action={
            <Button onClick={() => void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())}>
              Retry
            </Button>
          }
        >
          Could not finish searching chats. Results already found are kept; retry to continue.
        </Alert>
      )}
      <TripPlannerDataGrid
        apiRef={gridApiRef}
        rows={rows}
        columns={columns}
        pagination
        pageSizeOptions={[20, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: CHAT_REVIEW_BATCH_SIZE } } }}
        loading={query.isLoading}
        getRowHeight={() => 'auto'}
        localeText={{
          noRowsLabel: searching
            ? 'Searching for matching chats…'
            : paused
              ? 'Search paused'
              : 'No matching chats found',
        }}
        onRowClick={({ row }) => openChat(row)}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-overlayWrapper': { minHeight: 80 } }}
      />
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
        {query.hasNextPage && (
          <Button variant="contained" disabled={searching} sx={{ textTransform: 'none' }} onClick={loadMore}>
            {paused ? 'Continue searching' : 'Find more chats'}
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          {searching
            ? 'Searching successive pages for matches…'
            : query.hasNextPage
              ? 'More chats are available to search.'
              : query.isError
                ? ''
                : 'All matching chats loaded.'}
        </Typography>
      </Stack>
      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
        <TripSpoofDialog
          openModal={Boolean(spoof.url)}
          flushSpoofingState={() => setSpoof({ url: '', name: '', copied: false })}
          spoofUrl={spoof.url}
          trip={spoof.name}
          copiedToClipboard={spoof.copied}
        />
      </PermissionedComponent>
    </>
  );
}
